/**
 * Turning a plan into the exact list of buffers the graph will play.
 *
 * This module exists because every audible defect this project has shipped lived in
 * arithmetic that could not be looked at. Scheduling was tangled into `WebAudioEngine`,
 * where it needed an `AudioContext`, a network, and a wall clock to run at all — so it
 * had no tests, and a 250 ms hole in every gap of every session went unnoticed until
 * someone sat through one.
 *
 * Pulled out here, the same arithmetic is a pure function from (recording, plan) to a
 * list of ops. It can be checked over the whole corpus in seconds without decoding a
 * byte, it can be replayed offline to produce a file you can listen to, and the live
 * engine becomes a thin executor of the list rather than the place the list is invented.
 *
 * `scheduleOps` is the single source of truth. The engine, the offline renderer and the
 * corpus sweep all call it. Nothing may re-derive a schedule anywhere else — a second
 * implementation is exactly how `pipeline/render.py` came to be unable to reproduce a
 * client bug.
 *
 * ---------------------------------------------------------------- the join convention
 *
 * Every join between two ops is an equal-power crossfade over one shared window of
 * CROSSFADE_SEC. Both sides fade across the *same* interval: one out, one in. If they
 * fade over adjacent intervals instead, the sum dips to silence in the middle, and if
 * one fades while the other plays flat, the sum bumps.
 *
 * Where that window sits depends on which side can supply extra material:
 *
 *   speech -> tone   the window ends at the boundary, [b - xf, b]. Speech is a finite
 *                    decoded buffer with nothing past its end, so it fades out over its
 *                    own final xf, which lands in the 0.3 s of room tone that
 *                    segmentation pads every island with. The tone starts xf early.
 *
 *   tone -> tone     the window starts at the boundary, [b, b + xf]. Tone is generated,
 *                    so the outgoing chunk simply renders xf more of itself. The two
 *                    chunks are independent noise draws from the same spectrum, which is
 *                    precisely the case an equal-power crossfade is correct for.
 *
 *   tone -> speech   the window starts at the boundary, [b, b + xf], same reason.
 *
 *   speech -> speech (interior parts of one island) no fade at all. Those are contiguous
 *                    samples cut at the quietest moment available, and fading there
 *                    would carve a dip out of real audio.
 *
 *   speech -> speech (across a cue trim) is a hard cut. A crossfade needs source handles
 *                    outside the retained intervals; absent an explicit reviewed handle,
 *                    reading those samples would put excluded speech back into the mix.
 *
 * The rule that falls out, and that the tests enforce: **the ops must tile
 * [0, actualSec) with no hole, and every overlap must be exactly one declared crossfade
 * window with one op fading out and the other fading in.**
 */

import type { Recording, SessionPlan } from '../types';
import type { Container } from './containers.ts';
import { CHUNK_SEC, islandParts } from './parts.ts';
import { gapToneRms } from './tone-level.ts';

/** Equal-power crossfade at every join. Measured invisible: 0.23–0.60 dB level step. */
export const CROSSFADE_SEC = 0.25;
/** Click guard kept wholly inside each retained side of a hard exclusion. */
export const CUT_EDGE_FADE_SEC = 0.005;

/**
 * Where a sample came from.
 *
 * This is the spine of the QA architecture (docs/QA-ARCHITECTURE.md). "Anything which is
 * not native continuous audio that arrived from the import" is only a checkable claim if
 * every sample carries its origin, so every op declares one and the renderer writes them
 * out beside the audio.
 */
export type Provenance =
  | {
      kind: 'native';
      recordingId: string;
      island: number;
      part: number;
      /** The span of the ORIGINAL recording this op reproduces. */
      srcStart: number;
      srcEnd: number;
      container?: Container;
    }
  | {
      kind: 'synth';
      recordingId: string;
      /** Index into the plan's effective gaps. */
      gap: number;
      /** Which chunk of that gap, from 0. */
      chunk: number;
      seed: number;
      profile?: string;
      /** Shared web/native level; absent only for legacy profile-level behaviour. */
      targetRms?: number;
    }
  /** Digital silence. Legitimate only for `tone: 'inaudible'` recordings. */
  | { kind: 'silence'; recordingId: string; gap: number; chunk: number };

/** One buffer attached to the graph. The engine's real unit of work. */
export interface ScheduleOp {
  /** Session time this op begins sounding, including its fade-in. Seconds. */
  atSec: number;
  /** How long it sounds, including both fades. Seconds. */
  durSec: number;
  /** Offset into its own buffer at which playback starts. Seconds. */
  offsetSec: number;
  /** Length of the equal-power fade at the head. 0 for none. */
  fadeInSec: number;
  /** Length of the equal-power fade at the tail. 0 for none. */
  fadeOutSec: number;
  prov: Provenance;
  /**
   * True when `durSec` was cut short by the buffer being smaller than the op wanted.
   * Always a finding: it means the codec produced less audio than the manifest claims.
   */
  clipped?: boolean;
}

/** A run of output with one provenance and source time advancing at exactly 1.0x. */
export interface ProvenanceSpan {
  fromSec: number;
  toSec: number;
  prov: Provenance;
}

/** A boundary the analyser must inspect. */
export type StitchKind =
  | 'speech->tone'
  | 'tone->speech'
  | 'tone->tone'
  | 'speech->speech-contiguous'
  | 'speech->speech-trimmed'
  | 'start'
  | 'end';

export interface Stitch {
  atSec: number;
  kind: StitchKind;
  before?: Provenance;
  after?: Provenance;
  /** The crossfade window, if the two sides overlap. */
  window?: [number, number];
}

export interface ScheduleOptions {
  /**
   * Seconds of tone rendered per buffer. Long silences are chunked so a 30-minute gap
   * never becomes a 30-minute buffer. Defaults to CHUNK_SEC from ./tone.
   */
  chunkSec?: number;
  /**
   * Actual decoded duration of one island part, when it is known. The manifest's
   * `srcEnd - srcStart` is nominal; Opus and AAC both return something slightly
   * different, and the difference is what `clipped` is about. Return undefined to use
   * the nominal length.
   */
  bufferDurationSec?: (island: number, part: number) => number | undefined;
  /** Which container the device chose. Recorded in provenance; does not affect timing. */
  container?: Container;
  /**
   * Only emit ops that begin before this session time. The live engine passes its
   * horizon; offline callers leave it Infinity. Ops are never split by the horizon — an
   * op that starts inside it is emitted whole.
   */
  untilSec?: number;
  /** Where to resume from. Ops entirely before this are not emitted. */
  fromSec?: number;
}

/**
 * The complete list of buffers for a session, in time order.
 *
 * Pure: same recording, plan and options in, same ops out, on any machine, with no
 * audio context and no network. This is what makes the whole QA stack possible.
 */
export function scheduleOps(
  rec: Recording,
  plan: SessionPlan,
  opts?: ScheduleOptions,
): ScheduleOp[] {
  const chunkSec = opts?.chunkSec ?? CHUNK_SEC;
  if (!(chunkSec > 0) || !Number.isFinite(chunkSec)) {
    throw new Error(`chunkSec must be finite and positive, got ${chunkSec}`);
  }
  if (chunkSec < CROSSFADE_SEC) {
    throw new Error(`chunkSec must be at least the ${CROSSFADE_SEC}s crossfade`);
  }

  // Speech first, tone second.
  //
  // Tone is the flexible side of every join — it is generated, so it can start earlier or
  // run longer at no cost — which means a tone op's fades have to match whatever the
  // speech beside it actually turned out to be. Speech is not flexible: an island cut into
  // 5 ms parts can only fade over 2.5 ms, and a part that decoded short ends where it ends.
  //
  // Deciding the tone's overlaps from the plan's nominal geometry, before knowing that, is
  // how a red-team pass found sessions with three ops fading on one boundary, tone
  // scheduled before time zero, and 20 ms holes at part seams. So speech is built first and
  // tone is fitted to it.
  const speechOps = new Map<number, ScheduleOp[]>();
  /** Where each speech segment's audio really ends, once short decodes are accounted for. */
  const speechEnd = new Map<number, number>();

  for (let s = 0; s < plan.timeline.length; s++) {
    const seg = plan.timeline[s];
    if (seg.kind !== 'speech') continue;
    const built = buildSpeech(rec, plan, s, opts);
    speechOps.set(s, built.ops);
    speechEnd.set(s, built.endSec);
  }

  const ops: ScheduleOp[] = [];
  for (let s = 0; s < plan.timeline.length; s++) {
    const seg = plan.timeline[s];
    if (seg.kind === 'speech') {
      ops.push(...(speechOps.get(s) ?? []));
      continue;
    }

    // How much this gap has to overlap its neighbours. Where a neighbouring speech op
    // exists, its own fade is the answer — not CROSSFADE_SEC, which it may be too short
    // to carry. Where the neighbour is absent (a zero-length speech segment between two
    // gaps, or the plan simply beginning or ending here) the tone-to-tone rule applies,
    // except at the session's own edges where there is nothing to fade against.
    const prevOps = speechOps.get(s - 1);
    const nextOps = speechOps.get(s + 1);
    const prevLast = prevOps?.length ? prevOps[prevOps.length - 1] : null;
    const nextFirst = nextOps?.length ? nextOps[0] : null;
    const targetRms = gapToneRms(rec,
      prevLast?.prov.kind === 'native' ? prevLast.prov.srcEnd : undefined,
      nextFirst?.prov.kind === 'native' ? nextFirst.prov.srcStart : undefined);
    const isSessionEnd = s === plan.timeline.length - 1;

    // Speech is a finite buffer with nothing past its end, so it fades out over its own
    // last moments and the tone starts early to meet it: window `[b - f, b]`, where `f`
    // is whatever fade that speech op could actually carry. Tone can render more of
    // itself, so tone meets tone the other way round: window `[b, b + f]`. Where a speech
    // segment exists in the plan but produced no ops — a zero-length island — the two
    // gaps are neighbours in fact and must both use the tone-to-tone form, or one fades
    // out over `[b, b+f]` while the other fades in over `[b-f, b]` and the sum dips to
    // silence between them.
    const preRoll = prevLast ? prevLast.fadeOutSec : 0;
    const postRoll = isSessionEnd ? 0 : (nextFirst ? nextFirst.fadeInSec : CROSSFADE_SEC);
    const headFade = prevLast ? prevLast.fadeOutSec : CROSSFADE_SEC;
    const tailFade = nextFirst ? nextFirst.fadeInSec : CROSSFADE_SEC;

    // A short decode upstream leaves the previous island ending early. Tone is generated,
    // so it simply starts there and renders a little more of itself; the session keeps its
    // length and no hole opens at the seam.
    const prevEnd = speechEnd.get(s - 1);
    const segStart = prevEnd !== undefined ? Math.min(seg.startSec, prevEnd) : seg.startSec;

    // Chunk boundaries, computed rather than stepped, so the last chunk of a gap can
    // never be a runt. A chunk shorter than two crossfades has to shrink its own fades,
    // which puts three ops on one boundary with windows that no longer line up —
    // measured on 9 of 100 thirty-minute sessions and 51 of 234 sixty-minute ones,
    // landing exactly where he starts speaking again. A short remainder is absorbed
    // into the chunk before it instead.
    const bounds: number[] = [segStart];
    for (let t = segStart + chunkSec; t < seg.endSec - 1e-9; t += chunkSec) {
      bounds.push(t);
    }
    bounds.push(seg.endSec);
    if (bounds.length > 2
        && bounds[bounds.length - 1] - bounds[bounds.length - 2] < 2 * CROSSFADE_SEC) {
      bounds.splice(bounds.length - 2, 1);
    }

    for (let b = 0; b < bounds.length - 1; b++) {
      const from = bounds[b];
      const to = bounds[b + 1];
      const first = b === 0;
      const last = b === bounds.length - 2;
      // Never before zero: a fade placed in the past is scheduled late on a real clock,
      // and the session starts behind itself.
      const atSec = first ? Math.max(0, from - preRoll) : from;
      // Interior chunk seams always overlap by a full crossfade — both sides are noise
      // from the same spectrum and can render as much of themselves as they like. Only
      // the gap's last chunk is governed by what comes after it, and at the end of a
      // session that is nothing at all, so the fade has to land inside `actualSec`
      // rather than a quarter second beyond it where a renderer will cut it at full gain.
      const durSec = to - atSec + (last ? postRoll : CROSSFADE_SEC);
      const common = {
        recordingId: rec.id,
        gap: seg.gap,
        chunk: b,
      };
      const prov: Provenance = rec.toneProfile
        ? {
            kind: 'synth',
            ...common,
            seed: Math.round(from * 1000) + 1,
            profile: rec.toneProfile,
            ...(targetRms === undefined ? {} : { targetRms }),
          }
        // Digital silence is legitimate for `inaudible` and for nothing else. A recording
        // with a strategy we cannot render — a `loop` with no profile — must say so rather
        // than quietly play an hour of nothing and have every level detector accept it.
        : rec.tone === 'inaudible'
          ? { kind: 'silence', ...common }
          : (() => {
              throw new ToneStrategyError(rec.id, rec.tone);
            })();
      ops.push({
        atSec,
        durSec,
        offsetSec: 0,
        fadeInSec: Math.min(first ? headFade : CROSSFADE_SEC, durSec / 2),
        fadeOutSec: Math.min(last ? tailFade : CROSSFADE_SEC, durSec / 2),
        prov,
      });
    }
  }

  ops.sort((a, b) => a.atSec - b.atSec);
  return selectWindow(ops, plan.actualSec, opts);
}

/**
 * Collapse the ops into provenance spans.
 *
 * Inside a crossfade window two provenances sound at once; the span boundary is placed
 * at the window's midpoint and the window is reported separately by `stitches`, so the
 * analyser can exclude it rather than measure a mixture and call it a room.
 */
export function provenanceSpans(ops: ScheduleOp[]): ProvenanceSpan[] {
  if (ops.length === 0) return [];
  const sorted = [...ops].sort((a, b) => a.atSec - b.atSec);
  const bounds = sorted.map((op) => ({
    fromSec: op.atSec,
    toSec: op.atSec + op.durSec,
  }));

  for (let i = 1; i < sorted.length; i++) {
    const before = bounds[i - 1];
    const after = bounds[i];
    if (sameTime(after.fromSec, before.toSec)) {
      const boundary = (after.fromSec + before.toSec) / 2;
      before.toSec = boundary;
      after.fromSec = boundary;
      continue;
    }
    if (after.fromSec > before.toSec) continue;
    const overlapEnd = Math.min(before.toSec, after.toSec);
    const midpoint = (after.fromSec + overlapEnd) / 2;
    before.toSec = midpoint;
    after.fromSec = midpoint;
  }

  return bounds.flatMap((bound, i) => {
    if (bound.toSec <= bound.fromSec) return [];
    const op = sorted[i];
    const headSec = bound.fromSec - op.atSec;
    const tailSec = op.atSec + op.durSec - bound.toSec;
    return [{
      fromSec: bound.fromSec,
      toSec: bound.toSec,
      prov: sliceProvenance(op.prov, headSec, tailSec),
    }];
  });
}

/** Every boundary worth inspecting, including the session's own start and end. */
export function stitches(ops: ScheduleOp[], durationSec: number): Stitch[] {
  const sorted = [...ops].sort((a, b) => a.atSec - b.atSec);
  if (sorted.length === 0) {
    return [{ atSec: 0, kind: 'start' }, { atSec: durationSec, kind: 'end' }];
  }

  const spans = provenanceSpans(sorted);
  const atStart = spans.find((span) => span.fromSec <= 0 && span.toSec > 0) ?? spans[0];
  const atEnd = [...spans].reverse().find(
    (span) => span.fromSec < durationSec && span.toSec >= durationSec,
  ) ?? spans[spans.length - 1];
  const out: Stitch[] = [{ atSec: 0, kind: 'start', after: atStart?.prov }];

  for (let i = 1; i < sorted.length; i++) {
    const before = sorted[i - 1];
    const after = sorted[i];
    const overlapFrom = after.atSec;
    const overlapTo = Math.min(before.atSec + before.durSec, after.atSec + after.durSec);
    const window: [number, number] | undefined = overlapTo > overlapFrom + 1e-9
      ? [overlapFrom, overlapTo]
      : undefined;
    out.push({
      atSec: window ? (window[0] + window[1]) / 2 : after.atSec,
      kind: stitchKind(before.prov, after.prov),
      before: before.prov,
      after: after.prov,
      ...(window ? { window } : {}),
    });
  }

  out.push({ atSec: durationSec, kind: 'end', before: atEnd?.prov });
  return out;
}

/**
 * Stretches of the session no op covers. Must always be empty.
 *
 * This is the check that, run over the live corpus, found 250 ms of digital silence in
 * every gap of every session the product has ever played.
 */
export function coverageGaps(
  ops: ScheduleOp[],
  durationSec: number,
): { fromSec: number; toSec: number }[] {
  const gaps: { fromSec: number; toSec: number }[] = [];
  let coveredTo = 0;
  const sorted = [...ops].sort((a, b) => a.atSec - b.atSec);
  for (const op of sorted) {
    const fromSec = Math.max(0, op.atSec);
    const toSec = Math.min(durationSec, op.atSec + op.durSec);
    if (toSec <= 0 || fromSec >= durationSec || toSec <= fromSec) continue;
    if (fromSec > coveredTo + 1e-9) gaps.push({ fromSec: coveredTo, toSec: fromSec });
    coveredTo = Math.max(coveredTo, toSec);
    if (coveredTo >= durationSec) break;
  }
  if (coveredTo < durationSec - 1e-9) gaps.push({ fromSec: coveredTo, toSec: durationSec });
  return gaps;
}

/**
 * Overlaps that are not a declared crossfade: two ops sounding at once where one is not
 * fading out while the other fades in, or where the two fade windows disagree.
 */
export function badOverlaps(
  ops: ScheduleOp[],
): { fromSec: number; toSec: number; reason: string }[] {
  const bad: { fromSec: number; toSec: number; reason: string }[] = [];
  const sorted = [...ops].sort((a, b) => a.atSec - b.atSec);
  for (let i = 0; i < sorted.length; i++) {
    const before = sorted[i];
    const beforeEnd = before.atSec + before.durSec;
    for (let j = i + 1; j < sorted.length && sorted[j].atSec < beforeEnd - 1e-9; j++) {
      const after = sorted[j];
      const fromSec = after.atSec;
      const toSec = Math.min(beforeEnd, after.atSec + after.durSec);
      if (toSec <= fromSec + 1e-9) continue;
      if (before.fadeOutSec <= 0 || after.fadeInSec <= 0) {
        bad.push({ fromSec, toSec, reason: 'overlap has no matching fade-out and fade-in' });
        continue;
      }
      const fadeOutFrom = beforeEnd - before.fadeOutSec;
      const fadeInTo = after.atSec + after.fadeInSec;
      if (!sameTime(fadeOutFrom, fromSec) || !sameTime(fadeInTo, toSec)
          || !sameTime(beforeEnd, toSec)) {
        bad.push({ fromSec, toSec, reason: 'overlap and fade windows do not match' });
      }
    }
  }
  return bad;
}

/**
 * A recording whose silence we do not know how to make.
 *
 * `synth` renders from a profile and `inaudible` is legitimately digital silence. A `loop`
 * strategy has a donor recording rather than a profile, and nothing here implements it —
 * so rather than fall through to silence and have an hour of nothing pass every level
 * check, say so. There are zero loop recordings in the corpus today, which is exactly why
 * this would otherwise ship unnoticed.
 *
 * Fields are assigned explicitly rather than through parameter properties: Node's
 * type-stripping loader cannot rewrite those, and `.mjs` tools import this module directly.
 */
export class ToneStrategyError extends Error {
  readonly recordingId: string;
  readonly strategy: string;

  constructor(recordingId: string, strategy: string) {
    super(`no tone renderer for strategy "${strategy}" (${recordingId})`);
    this.name = 'ToneStrategyError';
    this.recordingId = recordingId;
    this.strategy = strategy;
  }
}

function sourceIsContiguous(
  rec: Recording,
  beforeIsland: number,
  beforeEnd: number,
  afterIsland: number,
  afterStart: number,
): boolean {
  return beforeIsland === afterIsland
    && sameTime(beforeEnd, afterStart)
    && rec.speech[beforeIsland] === rec.speech[afterIsland];
}

/**
 * One speech segment's ops, and where its audio really ends.
 *
 * Parts are chained by what they actually decoded, not by where the manifest says they
 * are. Nominal placement opens a hole at every seam the moment a codec returns less than
 * it promised, and it does: two parts of one recording came back a single sample short and
 * an Opus part came back 32.6 ms short, on the first real renders. Positive slop changes
 * nothing — the op is still cut to what it wanted — so only shortfalls move anything, and
 * they move the following gap earlier rather than leaving a void.
 *
 * The one case this cannot repair is a session that *ends* on a short part: there is no
 * following gap to absorb it, and no audio to invent. That is D-011, and it belongs to the
 * pipeline.
 */
function buildSpeech(
  rec: Recording,
  plan: SessionPlan,
  s: number,
  opts?: ScheduleOptions,
): { ops: ScheduleOp[]; endSec: number } {
  const seg = plan.timeline[s];
  if (seg.kind !== 'speech') return { ops: [], endSec: plan.timeline[s].endSec };

  const isl = rec.speech[seg.island];
  const parts = islandParts(rec, seg.island);
  const from = seg.srcStart ?? isl.srcStart;
  const to = seg.srcEnd ?? (
    seg.srcStart !== undefined ? isl.srcEnd : isl.srcStart + (seg.endSec - seg.startSec)
  );
  const next = plan.timeline[s + 1];
  const previous = plan.timeline[s - 1];
  const contiguousBefore = previous?.kind === 'speech'
    && sourceIsContiguous(rec, previous.island,
      previous.srcEnd ?? rec.speech[previous.island].srcEnd,
      seg.island, from);
  const contiguousAfter = next?.kind === 'speech'
    && sourceIsContiguous(rec, seg.island, to, next.island,
      next.srcStart ?? rec.speech[next.island].srcStart);
  const wanted = parts.flatMap((part, partIndex) => {
    const a = Math.max(from, part.srcStart);
    const b = Math.min(to, part.srcEnd);
    if (b <= a) return [];
    return [{ part, partIndex, a, b }];
  });

  const ops: ScheduleOp[] = [];
  let cursor = seg.startSec;
  for (let p = 0; p < wanted.length; p++) {
    const { part, partIndex, a, b } = wanted[p];
    const first = p === 0;
    const last = p === wanted.length - 1;
    const offsetSec = a - part.srcStart;
    const wantSec = b - a;
    const decodedBufferSec = opts?.bufferDurationSec?.(seg.island, partIndex);
    const availableSec = Math.max(0, decodedBufferSec === undefined
      ? part.srcEnd - a
      : decodedBufferSec - offsetSec);
    const durSec = Math.min(wantSec, availableSec);
    if (durSec <= 0) continue;
    const fadeLimit = durSec / 2;
    const op: ScheduleOp = {
      atSec: cursor,
      durSec,
      offsetSec,
      // A hard exclusion supplies no source handle and therefore cannot overlap. A 5 ms
      // edge fade stays wholly inside each retained interval to suppress a discontinuity.
      fadeInSec: first
        ? Math.min(previous?.kind === 'speech' && !contiguousBefore
          ? CUT_EDGE_FADE_SEC : previous?.kind === 'speech' ? 0 : CROSSFADE_SEC, fadeLimit)
        : 0,
      fadeOutSec: last
        ? Math.min(next?.kind === 'speech' && !contiguousAfter
          ? CUT_EDGE_FADE_SEC : next?.kind === 'speech' ? 0 : CROSSFADE_SEC, fadeLimit)
        : 0,
      prov: {
        kind: 'native',
        recordingId: rec.id,
        island: seg.island,
        part: partIndex,
        srcStart: a,
        srcEnd: a + durSec,
        container: opts?.container,
      },
    };
    if (durSec < wantSec) op.clipped = true;
    ops.push(op);
    cursor += durSec;
  }

  return { ops, endSec: cursor };
}

function selectWindow(
  ops: ScheduleOp[],
  durationSec: number,
  opts?: ScheduleOptions,
): ScheduleOp[] {
  const fromSec = opts?.fromSec;
  const untilSec = opts?.untilSec ?? Infinity;
  const mustProgress = fromSec !== undefined && fromSec >= 0 && fromSec < durationSec;
  const out: ScheduleOp[] = [];

  for (const op of ops) {
    const endSec = op.atSec + op.durSec;
    if (fromSec !== undefined && endSec <= fromSec) continue;
    if (op.atSec >= untilSec && (out.length > 0 || !mustProgress)) break;

    if (fromSec !== undefined && op.atSec < fromSec) {
      const advanceSec = fromSec - op.atSec;
      const durSec = op.durSec - advanceSec;
      // Resuming inside a crossfade cuts both sides of it. Keeping the outgoing op's full
      // fade-out would make it longer than the op now is, and dropping the incoming op's
      // fade-in altogether would leave two sources sounding with nothing declaring it —
      // both were found by a red-team pass, at all three kinds of join.
      //
      // What survives of the window is `[fromSec, b]`, so both sides fade over exactly
      // that: a shorter equal-power crossfade, still one shared window, still no dip and
      // no bump. An op resumed outside any fade keeps a fade-in of zero, which is the
      // right answer — it starts mid-signal, and smoothing that is the engine's business,
      // not the schedule's.
      out.push({
        ...op,
        atSec: fromSec,
        durSec,
        offsetSec: op.offsetSec + advanceSec,
        fadeInSec: Math.max(0, Math.min(op.fadeInSec - advanceSec, durSec)),
        fadeOutSec: Math.min(op.fadeOutSec, durSec),
        prov: sliceProvenance(op.prov, advanceSec, 0),
      });
    } else {
      out.push(op);
    }
  }
  return out;
}

function sliceProvenance(prov: Provenance, headSec: number, tailSec: number): Provenance {
  if (prov.kind !== 'native') return prov;
  return {
    ...prov,
    srcStart: prov.srcStart + headSec,
    srcEnd: prov.srcEnd - tailSec,
  };
}

function stitchKind(before: Provenance, after: Provenance): StitchKind {
  if (before.kind === 'native' && after.kind !== 'native') return 'speech->tone';
  if (before.kind !== 'native' && after.kind === 'native') return 'tone->speech';
  if (before.kind !== 'native' || after.kind !== 'native') return 'tone->tone';
  return before.island === after.island && sameTime(before.srcEnd, after.srcStart)
    ? 'speech->speech-contiguous'
    : 'speech->speech-trimmed';
}

function sameTime(a: number, b: number): boolean {
  return Math.abs(a - b) <= 1e-9;
}
